import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  PlusIcon,
  TrashIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';

interface Item {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  code?: string;
}

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string;
  cuit: string;
  dni: string;
  address: string;
  name?: string;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  type: string;
  clientId: string;
  clientName: string;
  clientDocument: string;
  clientTaxCondition: string;
  clientAddress: string;
  items: Item[];
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  afipCae: string;
  afipCbteTipo: number;
  afipCbteNro: number;
  afipPtoVta: number;
  status: string;
}

const REASONS = [
  { value: 'DEVOLUCION', label: 'Devolución' },
  { value: 'ERROR_FACTURACION', label: 'Error de facturación' },
  { value: 'DESCUENTO', label: 'Descuento posterior' },
  { value: 'ANULACION', label: 'Anulación total' },
  { value: 'OTRO', label: 'Otro' }
];

const CreateCreditNote: React.FC = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linkedInvoice, setLinkedInvoice] = useState<Invoice | null>(null);

  // Form state
  const [type, setType] = useState<'NCA' | 'NCB'>('NCB');
  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientDocument, setClientDocument] = useState('');
  const [clientTaxCondition, setClientTaxCondition] = useState('CONSUMIDOR_FINAL');
  const [clientAddress, setClientAddress] = useState('');
  const [reason, setReason] = useState('OTRO');
  const [notes, setNotes] = useState('');
  const [taxRate, setTaxRate] = useState(21);
  const [items, setItems] = useState<Item[]>([{ description: '', quantity: 1, unitPrice: 0, total: 0 }]);

  // Client search
  const [clients, setClients] = useState<Client[]>([]);
  const [clientSearch, setClientSearch] = useState('');

  // Invoice search (for manual mode)
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [showInvoiceSearch, setShowInvoiceSearch] = useState(false);

  useEffect(() => {
    if (invoiceId) {
      loadInvoice(invoiceId);
    }
    fetchClients();
  }, [invoiceId]);

  const loadInvoice = async (id: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`/api/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLinkedInvoice(data);
      setType(data.type === 'A' ? 'NCA' : 'NCB');
      setClientId(data.clientId);
      setClientName(data.clientName);
      setClientDocument(data.clientDocument || '');
      setClientTaxCondition(data.clientTaxCondition || 'CONSUMIDOR_FINAL');
      setClientAddress(data.clientAddress || '');
      const rate = Number(data.taxRate) || 21;
      const taxFactor = 1 + rate / 100;
      setTaxRate(rate);
      setNotes(`NC generada desde factura ${data.invoiceNumber}`);
      // En las facturas, unitPrice y total se guardan con IVA incluido.
      // La NC trabaja con precios netos (le suma IVA al mostrar/persistir),
      // así que convertimos a neto al cargar para no duplicar el IVA.
      setItems((data.items || []).map((item: Item) => {
        const qty = Number(item.quantity) || 1;
        const itemTotalCIva = Number(item.total) || qty * Number(item.unitPrice || 0);
        const itemTotalNeto = parseFloat((itemTotalCIva / taxFactor).toFixed(2));
        const unitPriceNeto = qty > 0
          ? parseFloat((itemTotalNeto / qty).toFixed(2))
          : 0;
        return {
          description: item.description,
          quantity: qty,
          unitPrice: unitPriceNeto,
          total: parseFloat((qty * unitPriceNeto).toFixed(2)),
          code: item.code || ''
        };
      }));
    } catch {
      toast.error('Error cargando factura');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/clients?limit=100', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(data.clients || []);
    } catch {}
  };

  const searchInvoices = async (term: string) => {
    if (!term || term.length < 2) { setInvoices([]); return; }
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`/api/invoices?search=${term}&status=authorized&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(data.invoices || []);
    } catch {}
  };

  useEffect(() => {
    const timer = setTimeout(() => searchInvoices(invoiceSearch), 300);
    return () => clearTimeout(timer);
  }, [invoiceSearch]);

  const selectInvoice = (inv: Invoice) => {
    loadInvoice(inv.id);
    setShowInvoiceSearch(false);
    setInvoiceSearch('');
  };

  const selectClient = (client: Client) => {
    setClientId(client.id);
    const name = client.companyName || client.name || `${client.firstName || ''} ${client.lastName || ''}`.trim();
    setClientName(name);
    setClientDocument(client.cuit || client.dni || '');
    setClientTaxCondition(client.cuit ? 'RESPONSABLE_INSCRIPTO' : 'CONSUMIDOR_FINAL');
    setClientAddress(client.address || '');
    setType(client.cuit ? 'NCA' : 'NCB');
  };

  const updateItem = (index: number, field: keyof Item, value: any) => {
    const newItems = [...items];
    (newItems[index] as any)[field] = value;
    if (field === 'quantity' || field === 'unitPrice') {
      newItems[index].total = Number(newItems[index].quantity) * Number(newItems[index].unitPrice);
    }
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unitPrice: 0, total: 0 }]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
  const taxAmount = (subtotal * taxRate) / 100;
  const total = subtotal + taxAmount;

  const fmtMoney = (n: number) => Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleSubmit = async () => {
    if (!clientName) { toast.error('Seleccione un cliente'); return; }
    if (items.length === 0 || items.every(i => !i.description)) { toast.error('Agregue al menos un item'); return; }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const validItems = items.filter(i => i.description);

      if (linkedInvoice) {
        // Create from invoice
        await axios.post(`/api/credit-notes/from-invoice/${linkedInvoice.id}`, {
          items: validItems,
          reason,
          notes
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        // Manual create
        await axios.post('/api/credit-notes', {
          type,
          clientId,
          clientName,
          clientDocument,
          clientTaxCondition,
          clientAddress,
          items: validItems,
          taxRate,
          reason,
          notes
        }, { headers: { Authorization: `Bearer ${token}` } });
      }

      toast.success('Nota de crédito creada exitosamente');
      navigate('/notas-credito');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error creando nota de crédito');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin h-8 w-8 border-2 border-gray-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/notas-credito')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
            {linkedInvoice ? `NC desde Factura ${linkedInvoice.invoiceNumber}` : 'Nueva Nota de Crédito'}
          </h1>
          {linkedInvoice && (
            <p className="text-sm text-muted-foreground mt-1">
              Tipo: {type} | CAE: {linkedInvoice.afipCae} | Total factura: $ {fmtMoney(linkedInvoice.total)}
            </p>
          )}
        </div>
      </div>

      {/* Buscar factura (modo manual) */}
      {!linkedInvoice && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <label className="text-sm font-medium">Vincular a factura autorizada (opcional)</label>
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar factura por número o cliente..."
              value={invoiceSearch}
              onChange={(e) => { setInvoiceSearch(e.target.value); setShowInvoiceSearch(true); }}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm"
            />
            {showInvoiceSearch && invoices.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {invoices.map(inv => (
                  <button
                    key={inv.id}
                    onClick={() => selectInvoice(inv)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm border-b last:border-0"
                  >
                    <span className="font-medium">{inv.invoiceNumber}</span> - {inv.clientName} - $ {fmtMoney(inv.total)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Datos del cliente */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <h2 className="text-sm font-semibold">Datos del Cliente</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Cliente</label>
            {linkedInvoice ? (
              <input type="text" value={clientName} readOnly className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-gray-900 text-sm" />
            ) : (
              <select
                value={clientId}
                onChange={(e) => {
                  const client = clients.find(c => c.id === e.target.value);
                  if (client) selectClient(client);
                }}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm"
              >
                <option value="">Seleccionar cliente...</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.companyName || c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim()}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="text-xs text-muted-foreground">CUIT/DNI</label>
            <input type="text" value={clientDocument} onChange={(e) => setClientDocument(e.target.value)} readOnly={!!linkedInvoice}
              className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-sm" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Tipo NC</label>
            <select value={type} onChange={(e) => setType(e.target.value as 'NCA' | 'NCB')} disabled={!!linkedInvoice}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm">
              <option value="NCA">NC A</option>
              <option value="NCB">NC B</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Motivo</label>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm">
              {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Alícuota IVA (%)</label>
            <input type="number" value={taxRate} onChange={(e) => setTaxRate(Number(e.target.value))} readOnly={!!linkedInvoice}
              className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-sm" />
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Items</h2>
          <Button variant="outline" size="sm" onClick={addItem}>
            <PlusIcon className="h-4 w-4 mr-1" /> Agregar
          </Button>
        </div>

        <div className="space-y-2">
          {items.map((item, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-5">
                <input
                  type="text"
                  placeholder="Descripción"
                  value={item.description}
                  onChange={(e) => updateItem(i, 'description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm"
                />
              </div>
              <div className="col-span-2">
                <input
                  type="number"
                  placeholder="Cant."
                  value={item.quantity}
                  onChange={(e) => updateItem(i, 'quantity', Number(e.target.value))}
                  min="0.01"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm text-right"
                />
              </div>
              <div className="col-span-2">
                <input
                  type="number"
                  placeholder="Precio"
                  value={item.unitPrice}
                  onChange={(e) => updateItem(i, 'unitPrice', Number(e.target.value))}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm text-right"
                />
              </div>
              <div className="col-span-2 text-right text-sm font-medium py-2">
                $ {fmtMoney(item.total)}
              </div>
              <div className="col-span-1 text-center">
                <button onClick={() => removeItem(i)} className="p-1 hover:bg-red-50 dark:hover:bg-red-900/30 rounded" disabled={items.length <= 1}>
                  <TrashIcon className="h-4 w-4 text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t pt-3 space-y-1 text-right">
          <div className="text-sm">Subtotal: <span className="font-medium">$ {fmtMoney(subtotal)}</span></div>
          <div className="text-sm">IVA ({taxRate}%): <span className="font-medium">$ {fmtMoney(taxAmount)}</span></div>
          <div className="text-lg font-bold">Total: $ {fmtMoney(total)}</div>
        </div>
      </div>

      {/* Notas */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <label className="text-sm font-medium">Observaciones</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full mt-2 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm resize-none"
          placeholder="Observaciones..."
        />
      </div>

      {/* Acciones */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/notas-credito')}>Cancelar</Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? 'Guardando...' : 'Crear Nota de Crédito'}
        </Button>
      </div>
    </div>
  );
};

export default CreateCreditNote;
